import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import type { PaymentMethodSlot } from "./types";

import { BillingErrorState } from "../BillingErrorState";
import { AddBackupMethodCard } from "./components/AddBackupMethodCard";
import { PaymentMethodCard } from "./components/PaymentMethodCard";
import { PaymentMethodModal } from "./components/PaymentMethodModal";
import { PaymentMethodsSkeleton } from "./components/PaymentMethodsSkeleton";
import { usePaymentMethods } from "./hooks/usePaymentMethods";
import { canAddBackupMethod } from "./utils/payment-method-slots";

interface PaymentMethodsTabProps {
  readonly isAddBackupOpen: boolean;
  readonly onAddBackupOpenChange: (isOpen: boolean) => void;
  readonly projectId: number;
}

export function PaymentMethodsTab({
  isAddBackupOpen,
  onAddBackupOpenChange,
  projectId,
}: PaymentMethodsTabProps) {
  const { data, isError, isPending, refetch } = usePaymentMethods(projectId);
  const [editingSlot, setEditingSlot] = useState<null | PaymentMethodSlot>(
    null,
  );
  const navigate = useNavigate({ from: "/billing/$projectId/payment-methods" });
  const isAddBackupLinkStale =
    isAddBackupOpen && !!data && !canAddBackupMethod(data);

  useEffect(() => {
    if (!isAddBackupLinkStale) return;

    navigate({
      replace: true,
      search: ({ modal: _modal, ...search }) => search,
    });
  }, [isAddBackupLinkStale, navigate]);

  if (isPending) {
    return <PaymentMethodsSkeleton />;
  }

  if (isError) {
    return (
      <BillingErrorState
        message="Failed to load payment methods."
        onRetry={() => refetch()}
      />
    );
  }

  const canAddBackup = canAddBackupMethod(data);
  const isModalOpen = !!editingSlot || (isAddBackupOpen && canAddBackup);

  const closeModal = () => {
    setEditingSlot(null);
    if (isAddBackupOpen) onAddBackupOpenChange(false);
  };

  return (
    <div className="flex flex-col gap-xl p-xl md:flex-row md:gap-3xl md:pt-3xl">
      <PaymentMethodCard
        method={data.defaultMethod}
        onUpdate={() => setEditingSlot({ kind: "default" })}
        slot={{ kind: "default" }}
      />

      {data.backupMethods.map((method, index) => (
        <PaymentMethodCard
          key={method.id}
          method={method}
          onUpdate={() => setEditingSlot({ index, kind: "backup" })}
          slot={{ index, kind: "backup" }}
        />
      ))}

      {canAddBackup && (
        <AddBackupMethodCard onAdd={() => onAddBackupOpenChange(true)} />
      )}

      {isModalOpen && (
        <PaymentMethodModal
          onClose={closeModal}
          paymentMethods={data}
          projectId={projectId}
          slot={editingSlot}
        />
      )}
    </div>
  );
}
